import type { FC, ReactNode } from 'react'

import { useEffect, useState } from 'react'
import { Menu, X } from 'lucide-react'
import { BallMark } from '@/components/landing/ball-mark'
import { cn } from '@/lib/utils'
import { NAV_LINKS } from '@/constants/event'

export const SiteNav: FC = (): ReactNode => {
    const [scrolled, setScrolled] = useState(false)
    const [open, setOpen] = useState(false)

    useEffect(() => {
        const onScroll = (): void => setScrolled(window.scrollY > 24)

        onScroll()
        window.addEventListener('scroll', onScroll, { passive: true })

        return () => window.removeEventListener('scroll', onScroll)
    }, [])

    useEffect(() => {
        if (!open) return

        const onKey = (event: KeyboardEvent): void => {
            if (event.key === 'Escape') setOpen(false)
        }

        window.addEventListener('keydown', onKey)

        return () => window.removeEventListener('keydown', onKey)
    }, [open])

    const solid = scrolled || open

    return (
        <header className='fixed inset-x-0 top-0 z-50 px-3 pt-3 md:px-6 md:pt-4'>
            <nav
                aria-label='Main'
                className={cn(
                    'mx-auto flex max-w-[1400px] items-center justify-between gap-4 rounded-full py-2 pl-3 pr-2 transition-[background-color,box-shadow] duration-300 md:pl-4',
                    solid ? 'bg-ink/85 shadow-[0_12px_40px_-12px_rgba(8,12,40,0.55)] backdrop-blur-md' : 'bg-transparent'
                )}
            >
                <a
                    href='/#hero'
                    className='flex items-center gap-2.5 rounded-full text-cream focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-lime'
                    onClick={() => setOpen(false)}
                >
                    <BallMark className='size-7' />
                    <span className='font-heading text-lg font-extrabold uppercase tracking-[0.06em]'>
                        Kitchen Open
                    </span>
                </a>

                <ul className='hidden items-center gap-8 lg:flex'>
                    {NAV_LINKS.map((link) => (
                        <li key={link.href}>
                            <a
                                href={`/${link.href}`}
                                className='rounded-sm text-sm font-medium text-cream/80 transition-colors hover:text-lime focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-lime'
                            >
                                {link.label}
                            </a>
                        </li>
                    ))}
                </ul>

                <div className='flex items-center gap-2'>
                    <a
                        href='/#tickets'
                        className='inline-flex min-h-10 items-center rounded-full bg-lime px-5 text-sm font-semibold text-ink transition-colors hover:bg-[#e2f86b] focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-lime'
                        onClick={() => setOpen(false)}
                    >
                        Tickets
                    </a>

                    <button
                        type='button'
                        aria-expanded={open}
                        aria-controls='mobile-menu'
                        aria-label={open ? 'Close menu' : 'Open menu'}
                        onClick={() => setOpen((value) => !value)}
                        className='inline-flex size-10 items-center justify-center rounded-full text-cream transition-colors hover:bg-cream/10 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lime lg:hidden'
                    >
                        {open ? <X className='size-5' /> : <Menu className='size-5' />}
                    </button>
                </div>
            </nav>

            <div
                id='mobile-menu'
                hidden={!open}
                className='mx-auto mt-2 max-w-[1400px] rounded-3xl bg-ink/95 p-3 shadow-[0_24px_60px_-20px_rgba(8,12,40,0.7)] backdrop-blur-md lg:hidden'
            >
                <ul className='flex flex-col'>
                    {NAV_LINKS.map((link) => (
                        <li key={link.href}>
                            <a
                                href={`/${link.href}`}
                                onClick={() => setOpen(false)}
                                className='flex min-h-12 items-center justify-between rounded-2xl px-4 font-heading text-2xl font-bold uppercase tracking-[0.02em] text-cream transition-colors hover:bg-cream/10 hover:text-lime focus-visible:outline-2 focus-visible:outline-lime'
                            >
                                {link.label}
                            </a>
                        </li>
                    ))}
                </ul>
            </div>
        </header>
    )
}
